import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUp,
  faBagShopping,
  faBowlFood,
  faCircleInfo,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { useAppNavigate } from '../../context/AppContext'
import { useLayout } from '../../context/LayoutContext'
import { IProduct } from '../../types/products'
import { cartCount, cartTotal } from '../../utils/cart'
import { haptic } from '../../utils/fx'
import { RouteKind } from '../../utils/useRouteKind'
import { scrollToMenu } from '../app-header/AppHeader'
import './BottomDock.scss'

type Props = {
  cart: IProduct[]
  route: RouteKind
}

const c = 'bottom-dock'

const BottomDock = ({ cart, route }: Props) => {
  const { t } = useTranslation()
  const navigate = useAppNavigate()
  const { openInfo, setSearchOpen, searchOpen } = useLayout()
  const count = cartCount(cart)
  const [scrolled, setScrolled] = useState(false)
  const showTop = route === 'home' && scrolled && !searchOpen

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function goHome() {
    haptic()
    setSearchOpen(false)
    if (route === 'home') window.scrollTo({ top: 0, behavior: 'smooth' })
    else navigate('/')
  }

  function openSearch() {
    haptic()
    if (route !== 'home') navigate('/')
    setSearchOpen(true)
    window.setTimeout(scrollToMenu, 60)
  }

  return (
    <nav className={c} aria-label='main'>
      <div className={`${c}__bar`}>
        <button
          className={`${c}__tab${route === 'home' && !searchOpen ? ' is-active' : ''}`}
          onClick={goHome}
        >
          <span className={`${c}__morph${showTop ? ' is-top' : ''}`}>
            <FontAwesomeIcon icon={faBowlFood} />
            <FontAwesomeIcon icon={faArrowUp} />
          </span>
          <span>{showTop ? t('TO_TOP') : t('MENU')}</span>
        </button>
        <button
          className={`${c}__tab${searchOpen ? ' is-active' : ''}`}
          onClick={openSearch}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <span>{t('SEARCH').replace(/\.+$/, '').replace('…', '')}</span>
        </button>
        <button
          className={`${c}__tab${route === 'delivery' ? ' is-active' : ''}`}
          onClick={() => {
            haptic()
            openInfo()
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>{t('INFO')}</span>
        </button>
        <button
          className={`${c}__cart${count ? ' has-items' : ''}`}
          data-cart-target
          onClick={() => {
            haptic()
            navigate('/cart')
          }}
          aria-label={t('CART')}
        >
          <span className={`${c}__cart-icon`}>
            <FontAwesomeIcon icon={faBagShopping} />
            {count > 0 && (
              <span key={count} className={`${c}__badge`}>
                {count}
              </span>
            )}
          </span>
          <span className={`${c}__cart-text`}>
            {count > 0 ? `${cartTotal(cart)} lei` : t('CART')}
          </span>
        </button>
      </div>
    </nav>
  )
}

export default BottomDock
